import _ from "lodash";
import { formatPhoneDisplay } from "./phoneUtils";

const addressPart = (value) => {
  if (value === null || value === undefined) return "";
  return _.replace(String(value), ",", "").trim();
};

export const formatClientAddressLine = (address) => {
  if (!address || typeof address !== "object") return "";
  const zip = address.zip || address.zipCode || "";
  return [address.line1, address.line2, address.city, address.state, zip, address.country]
    .map(addressPart)
    .filter((part) => !_.isEmpty(part))
    .join(" ");
};

const pickClientAddress = (client) => {
  const addresses = Array.isArray(client?.addresses)
    ? client.addresses.filter(Boolean)
    : [];
  const chosen =
    addresses.find((item) => item.isDefault) || addresses[addresses.length - 1];
  if (chosen) return formatClientAddressLine(chosen);

  if (typeof client?.address === "string") return client.address;
  if (client?.address && typeof client.address === "object") {
    return formatClientAddressLine(client.address);
  }
  return "";
};

const pickClientEmail = (client) => {
  if (client?.email) return client.email;
  const emails = Array.isArray(client?.emails) ? client.emails : [];
  const chosen = emails.find((item) => item?.isDefault) || emails[0];
  return chosen?.value ?? "";
};

const pickClientPhone = (client) => {
  const primary = formatPhoneDisplay(client?.phone);
  if (primary) return primary;
  const phones = Array.isArray(client?.phones) ? client.phones : [];
  const chosen = phones.find((item) => item?.isDefault) || phones[0];
  return chosen ? formatPhoneDisplay(chosen) : "";
};

export const clientSnapshotNeedsReload = (client) => {
  if (!client?._id) return false;
  const missingAddress = !Array.isArray(client.addresses);
  const missingPhone = client.phone === undefined && !Array.isArray(client.phones);
  return missingAddress || missingPhone;
};

export const clientToRelatedPerson = (client, { role = "person" } = {}) => {
  const person = {
    name: client?.name ?? "",
    cname: client?.cname ?? "",
    idType: client?.idType ?? "",
    id: client?.id ?? "",
    gender: client?.gender ?? "",
    email: pickClientEmail(client),
    birthday: client?.birthday ?? null,
    marketingPreference: client?.marketingPreference ?? "",
    phone: pickClientPhone(client),
    address: pickClientAddress(client),
  };

  if (role === "trustee") {
    return {
      name: person.name,
      idType: person.idType,
      id: person.id,
      email: person.email,
      address: person.address,
      marketingPreference: person.marketingPreference,
    };
  }

  return person;
};

const PERSON_IDENTITY_FIELDS = [
  "name",
  "cname",
  "idType",
  "id",
  "gender",
  "email",
  "phone",
  "address",
  "marketingPreference",
];

const TRUSTEE_IDENTITY_FIELDS = [
  "name",
  "idType",
  "id",
  "email",
  "address",
  "marketingPreference",
];

export const linkedClientId = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value._id || null;
  return value;
};

const clearIdentityFields = (record, fields) => {
  const next = { ...record };
  fields.forEach((key) => {
    next[key] = "";
  });
  next.birthday = null;
  return next;
};

export const stripLinkedTrustee = (trustee) => {
  if (!trustee || typeof trustee !== "object") return trustee;
  if (!linkedClientId(trustee.sourceClient)) {
    const next = { ...trustee };
    delete next.identityFromClient;
    return next;
  }
  const next = clearIdentityFields(trustee, TRUSTEE_IDENTITY_FIELDS);
  delete next.birthday;
  delete next.identityFromClient;
  next.sourceClient = linkedClientId(trustee.sourceClient);
  return next;
};

export const stripLinkedPersonIdentity = (person) => {
  if (!person || typeof person !== "object") return person;
  const next = { ...person };
  if (linkedClientId(next.sourceClient)) {
    Object.assign(next, clearIdentityFields(next, PERSON_IDENTITY_FIELDS));
    next.sourceClient = linkedClientId(person.sourceClient);
  }
  delete next.identityFromClient;
  if (next.trustee && typeof next.trustee === "object") {
    next.trustee = stripLinkedTrustee(next.trustee);
  }
  return next;
};
